import { addToast, Button, Modal } from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../components/ozmoit/common';
import { TranslationsKeys } from '../../../../locales/translations';
import { Collection } from '../../interfaces/collection';
import { generateSalenote } from '../actions/actions';

export type GenerateSalenoteModalMethods = {
  open: (next: Collection) => void;
  close: () => void;
};

type GenerateSalenoteModalProps = {
  onSave?: () => void;
};

const GenerateSalenoteModal = (
  { onSave }: GenerateSalenoteModalProps,
  ref: Ref<GenerateSalenoteModalMethods>,
) => {
  const { t } = useTranslation(TranslationsKeys.PAYMENT_HUB_NON_SALENOTES);
  const [collection, setCollection] = useState<Collection>();

  const { action: generate, loading } = useActionKitImp(generateSalenote, {
    onSuccess: () => {
      addToast({
        color: 'success',
        text: t(`generateSalenoteModal.success`),
      });
      !!onSave && onSave();
    },
    onError: {
      unknown: (error) => {
        if (error?.data instanceof Error) {
          addToast({
            color: 'danger',
            text: error.data.message,
          });
        } else {
          addToast({
            color: 'danger',
            text: t(`generateSalenoteModal.failed`),
          });
        }
      },
    },
  });

  const handleOpen = useCallback((next: Collection) => {
    setCollection(next);
  }, []);

  const handleClose = useCallback(() => {
    setCollection(undefined);
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(async () => {
    if (loading) {
      return;
    }

    await generate({
      collectionId: collection?.id!,
    });
  }, [loading, generate, collection]);

  return (
    <Modal isOpen={!!collection} size="md" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2 className="text-dark fs-22 mb-0 w-100 text-center mb-3">
              {t(`generateSalenoteModal.title`)}
            </h2>
            <p className="fs-16 w-100 text-center">
              {t(`generateSalenoteModal.description`)}
            </p>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.cancel')}
              type="button"
              fullwidth
              outlined
              disabled={loading}
              onClick={handleClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              text={t('generateSalenoteModal.create')}
              type="button"
              fullwidth
              onClick={handleSave}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(GenerateSalenoteModal);
